import {
  Autocomplete,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { DbIngestConfigColumnSetInfos } from "../../../Core/Api/Types/db-ingest-config-column-set-infos";
import { produce } from "immer";
import { cacheService, ingestService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { AddCircle, RemoveCircle } from "@mui/icons-material";

export type ModalCreateColumnElementProps = {};
export type ModalCreateColumnProps = CommonModalProps<ModalCreateColumnElementProps>;

const ModalCreateColumn = React.forwardRef<any, ModalCreateColumnProps>((props, ref) => {
  const { closeModal } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();
  const { t } = useTranslation();
  const metadataFields = React.useMemo(() => cacheService.GetMetadata(), []);
  const allFields =
    metadataFields.filter((f) => f.isSearchable && (f.layer === "Asset" || f.layer === "Content")) ?? [];
  const [requestObject, setRequestObject] = React.useState<{ request: DbIngestConfigColumnSetInfos }>({
    request: new DbIngestConfigColumnSetInfos({
      label: "",
      isEditable: false,
      isHidden: false,
      isMandatory: false,
      isToFillDuringIngest: false,
      dependencies: [{ fieldId: null, activeForValues: null, values: null }],
    }),
  });

  const [isMandatory, setIsMandatory] = React.useState<boolean>(false);
  const [isHidden, setIsHidden] = React.useState<boolean>(false);
  const [isToFill, setIsToFill] = React.useState<boolean>(false);
  const [isEditable, setIsEditable] = React.useState<boolean>(false);

  const [defaultLabel, setDefaultLabel] = React.useState<string>("");

  const handleConfirmClick = () => {
    // call to add column
    ingestService.CreateIngestConfigColumnSet(requestObject.request).then((response) => {
      if (response.success) enqueueCustomSnackbar(t("alert:success.columnAdded"), "success");
    });
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal title={t("modal:title.createColumn")} closeModal={handleCancelClick} width={600}>
      {" "}
      <Typography fontWeight={500}>{t("modal:body.metaDataFieldReference")}</Typography>
      <Select
        size="small"
        sx={{ width: "100%", mt: 1, mb: 1 }}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        inputProps={{ "aria-label": "Without label" }}
        onChange={(e) => {
          setDefaultLabel(allFields.find((x) => x.id.toString() === e.target.value)?.label!);
          setRequestObject(
            produce((draft) => {
              draft.request.fieldId = e.target.value as number;
              draft.request = { ...draft.request };
            })
          );
        }}
      >
        {" "}
        <MenuItem key={t("field:placeholder.selectOption")} value={undefined}>
          {t("field:placeholder.selectOption")}
        </MenuItem>
        {allFields.map((field) => (
          <MenuItem key={field.id} value={field.id}>
            {field.label}
          </MenuItem>
        ))}
      </Select>
      <FormControl sx={{ mb: 3 }}>
        <InputLabel>{t("field:label.columnLabel")}</InputLabel>
        <OutlinedInput
          value={defaultLabel}
          placeholder={t("common:label")}
          id="external-reference"
          onChange={(e) => {
            setDefaultLabel(e.currentTarget.value);
            setRequestObject(
              produce((draft) => {
                draft.request.label = defaultLabel;
              })
            );
          }}
        />
      </FormControl>
      <FormControl sx={{ mb: 3 }}>
        <InputLabel>{t("field:label.orderPosition")}</InputLabel>
        <OutlinedInput
          placeholder={t("common:position")}
          id="placeholdername"
          type="number"
          onChange={(e) => {
            setRequestObject(
              produce((draft) => {
                draft.request.order = parseInt(e.currentTarget.value);
              })
            );
          }}
        />
      </FormControl>
      <OutlinedCheckbox
        mb={2}
        direction="row-reverse"
        checked={isMandatory}
        onClick={() => {
          setIsMandatory((prevState) => !prevState);
          setRequestObject(
            produce((draft) => {
              if (draft.request.isMandatory === null) draft.request.isMandatory = true;
              else draft.request.isMandatory = !draft.request.isMandatory;
            })
          );
        }}
      >
        <Typography fontWeight={500}>{t("field:label.isMandatory")}</Typography>
      </OutlinedCheckbox>
      <OutlinedCheckbox
        mb={2}
        direction="row-reverse"
        checked={isHidden}
        onClick={() => {
          setIsHidden((prevState) => !prevState);
          setRequestObject(
            produce((draft) => {
              if (draft.request.isHidden === null) draft.request.isHidden = true;
              else draft.request.isHidden = !draft.request.isHidden;
            })
          );
        }}
      >
        <Typography fontWeight={500}>{t("field:label.isHiddenByDefault")}</Typography>
      </OutlinedCheckbox>
      <OutlinedCheckbox
        mb={2}
        direction="row-reverse"
        checked={isToFill}
        onClick={() => {
          setIsToFill((prevState) => !prevState);
          setRequestObject(
            produce((draft) => {
              if (draft.request.isToFillDuringIngest === null) draft.request.isToFillDuringIngest = true;
              else draft.request.isToFillDuringIngest = !draft.request.isToFillDuringIngest;
            })
          );
        }}
      >
        <Typography fontWeight={500}>{t("field:label.isToFillDuringIngest")}</Typography>
      </OutlinedCheckbox>
      <OutlinedCheckbox
        mb={2}
        direction="row-reverse"
        checked={isEditable}
        onClick={() => {
          setIsEditable((prevState) => !prevState);
          setRequestObject(
            produce((draft) => {
              if (draft.request.isEditable === null) draft.request.isEditable = true;
              else draft.request.isEditable = !draft.request.isEditable;
            })
          );
        }}
      >
        <Typography fontWeight={500}>{t("field:label.isEditable")}</Typography>
      </OutlinedCheckbox>
      {requestObject.request.dependencies?.map((d, i) => (
        <>
          <FormControl sx={{ width: "100%", mb: 0.5 }}>
            <InputLabel shrink>{t("field:label.activeForFieldValues")}</InputLabel>
            <Autocomplete
              value={d.activeForValues ?? []}
              onChange={(e, value) => {
                setRequestObject(
                  produce((draft) => {
                    if (!draft.request.dependencies) return;
                    draft.request!.dependencies[i].activeForValues = value;
                  })
                );
              }}
              options={
                allFields
                  .find((x) => x.id === requestObject.request.fieldId)
                  ?.values?.filter((x) => x.value)
                  .map((x) => x.value!) ?? []
              }
              multiple
              freeSolo
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>
          <Typography fontWeight={500}>{t("field:label.dependencyMetadataField")}</Typography>
          <Select
            value={d.fieldId}
            size="small"
            sx={{ width: "100%", mt: 1, mb: 1 }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            onChange={(e) => {
              setRequestObject(
                produce((draft) => {
                  if (!draft.request.dependencies) return;
                  draft.request.dependencies[i].fieldId = e.target.value as number;
                  draft.request = { ...draft.request };
                })
              );
            }}
          >
            {allFields.map((field) => (
              <MenuItem key={field.id} value={field.id}>
                {field.label}
              </MenuItem>
            ))}
          </Select>
          <FormControl sx={{ mb: 3 }}>
            <InputLabel shrink>{t("field:label.metadataFieldReferenceValues")}</InputLabel>
            <Autocomplete
              value={d.values ?? []}
              onChange={(e, value) => {
                setRequestObject(
                  produce((draft) => {
                    if (!draft.request.dependencies) return;
                    draft.request!.dependencies[i].values = value;
                    draft.request = { ...draft.request };
                  })
                );
              }}
              options={allFields.find((x) => x.id === d.fieldId)?.values?.map((x) => x.value ?? "") ?? []}
              multiple
              freeSolo
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>
          {requestObject.request.dependencies!.length > 1 && (
            <Button
              onClick={(e) => {
                setRequestObject(
                  produce((draft) => {
                    draft.request.dependencies! = draft.request.dependencies!.filter((x, n) => i !== n);
                    draft.request = { ...draft.request };
                  })
                );
              }}
              endIcon={<RemoveCircle />}
            >
              {t("button:removeDependency")}
            </Button>
          )}
        </>
      ))}
      <Button
        onClick={() => {
          setRequestObject(
            produce((draft) => {
              draft.request.dependencies! = [
                ...draft.request.dependencies!,
                { fieldId: null, activeForValues: null, values: null },
              ];
              draft.request = { ...draft.request };
            })
          );
        }}
        endIcon={<AddCircle />}
      >
        {t("button:addDependency")}
      </Button>
      <Stack spacing={1}>
        <Stack spacing={1} mb={1.5}></Stack>
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick}>
            {t("common:confirm")}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalCreateColumn;
